import React, { useState, useRef, useEffect } from "react";

const AudioPlayer = ({ audioSrc, autoStartIn = 10 }) => {
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.5);

  const [countdown, setCountdown] = useState(autoStartIn);
  const [countdownActive, setCountdownActive] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(true);

  const countdownRef = useRef(null);
  const hasStartedOnceRef = useRef(false); // ⭐ Prevent countdown after first play
  const lastActionWasSkipOrAutoPlay = useRef(false); // ⭐ Control beep

  // 🔊 Beep sound (only for autoplay + skip)
  const playBeep = () => {
    if (!lastActionWasSkipOrAutoPlay.current) return; // ❗ No beep for manual play

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    osc.frequency.value = 1000;
    osc.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  };

  // ⭐ Load audio fully before starting countdown
  useEffect(() => {
    const audio = audioRef.current;

    audio.pause();
    audio.currentTime = 0;
    audio.volume = volume;

    setIsPlaying(false);
    setProgress(0);
    setCountdown(autoStartIn);
    setCountdownActive(false);
    setLoadingAudio(true);

    clearInterval(countdownRef.current);

    const handleReady = () => {
      setLoadingAudio(false);

      // Only start countdown on FIRST load
      if (!hasStartedOnceRef.current) {
        setCountdownActive(true);
        startCountdown();
      }
    };

    audio.addEventListener("canplaythrough", handleReady);

    return () => {
      audio.removeEventListener("canplaythrough", handleReady);
      clearInterval(countdownRef.current);
    };
  }, [audioSrc]);

  // ⏳ Countdown before autoplay
  const startCountdown = () => {
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startAudio(true); // autoplay
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ▶ Start audio playback
  const startAudio = (isAutoOrSkip = false) => {
    lastActionWasSkipOrAutoPlay.current = isAutoOrSkip; // control beep

    playBeep();

    const audio = audioRef.current;
    audio.play()
      .then(() => {
        setIsPlaying(true);
        setCountdownActive(false);
        hasStartedOnceRef.current = true; // ❗ Do NOT show countdown again
      })
      .catch(() => {});
    
    audio.onended = () => {
      setIsPlaying(false);
      setProgress(100);
    };
  };

  // ⏭ Skip countdown
  const skipCountdown = () => {
    clearInterval(countdownRef.current);
    setCountdown(0);
    setCountdownActive(false);
    startAudio(true); // skip triggers beep
  };

  // ⏯ Manual play/pause (NO beep)
  const togglePlay = () => {
    lastActionWasSkipOrAutoPlay.current = false; // ❗ disable beep for manual play

    const audio = audioRef.current;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true));
    }
  };

  // 📊 Track progress
  useEffect(() => {
    const audio = audioRef.current;
    const updateProgress = () => {
      if (audio.duration)
        setProgress((audio.currentTime / audio.duration) * 100);
    };

    audio.addEventListener("timeupdate", updateProgress);

    return () =>
      audio.removeEventListener("timeupdate", updateProgress);
  }, []);

  return (
    <div className="w-full max-w-lg mx-auto bg-gray-100 p-4 py-14 mt-4 rounded-lg shadow-lg">
      
      {/* LOADING */}
      {loadingAudio && (
        <p className="text-center text-blue-600 font-semibold mb-2">
          Loading audio...
        </p>
      )}

      {/* COUNTDOWN (ONLY first autoplay) */}
      {!loadingAudio &&
        countdownActive &&
        !hasStartedOnceRef.current && (
          <div className="text-center mb-2">
            <p className="text-purple-700 font-semibold text-lg">
              Audio starts in {countdown}s
            </p>
            <button
              onClick={skipCountdown}
              className="mt-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-1 rounded text-sm"
            >
              Skip
            </button>
          </div>
        )}

      {/* CONTROLS */}
      <div className="flex items-center justify-between mt-3">
        <button
          onClick={togglePlay}
          disabled={countdownActive || loadingAudio}
          className={`${
            countdownActive || loadingAudio
              ? "bg-gray-400"
              : "bg-blue-500 hover:bg-blue-700"
          } text-white font-bold py-2 px-4 rounded`}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>

        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          disabled={countdownActive || loadingAudio}
          onChange={(e) => {
            audioRef.current.volume = e.target.value;
            setVolume(e.target.value);
          }}
          className="w-1/4 mx-4"
        />
      </div>

      {/* PROGRESS BAR */}
      <div className="overflow-hidden h-2 mt-4 text-xs flex rounded bg-blue-200">
        <div
          style={{ width: `${progress}%` }}
          className="flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
        ></div>
      </div>

      <audio ref={audioRef} src={audioSrc} preload="auto" />
    </div>
  );
};

export default AudioPlayer;
